import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { ReviewerStoreProvider } from '@/components/features/nexus/reviewer/providers/ReviewerStoreProvider';
import { loadPersistedReviewerFilters } from '@/components/features/nexus/reviewer/reviewer-persistence';
import { ReviewerPage } from '@/components/features/nexus/reviewer/ReviewerPage';
import { DEFAULT_REVIEWER_FILTERS } from '@/components/features/nexus/reviewer/types';
import { PageLayout } from '@/features/page-layout/PageLayout';
import { ErrorBoundary, ErrorFallback } from '@/modus/ErrorBoundary';

/** `proposal` deep-links from a notification and opens the drawer on load. */
const reviewerSearchSchema = z.object({
  proposal: z.string().optional(),
  project: z.string().optional(),
});

export const Route = createFileRoute('/_protected/t/$tenantId/nexus/reviewer')({
  component: ReviewerRoute,
  validateSearch: reviewerSearchSchema,
});

function ReviewerRoute() {
  const { proposal, project } = Route.useSearch();
  const { tenantId } = Route.useParams();
  // Hydrate persisted filters at store-creation time (pre-render), the natural
  // hydration point — a project deep-link scopes instead and skips restore.
  const [persistedFilters] = useState(() => (project ? undefined : loadPersistedReviewerFilters(tenantId)));

  return (
    <ReviewerStoreProvider
      initialState={{
        openProposalId: proposal ?? null,
        filters: project
          ? { ...DEFAULT_REVIEWER_FILTERS, project_ids: [project] }
          : persistedFilters
            ? { ...DEFAULT_REVIEWER_FILTERS, ...persistedFilters }
            : undefined,
      }}
    >
      <PageLayout header>
        <ErrorBoundary fallback={(err) => <ErrorFallback error={err} />}>
          <ReviewerPage />
        </ErrorBoundary>
      </PageLayout>
    </ReviewerStoreProvider>
  );
}
